import type {
	AccountInfo,
	AuthenticationResult,
	EventMessage,
	IPublicClientApplication,
	PopupRequest,
	SilentRequest,
} from "@azure/msal-browser";
import {
	ClientAuthError,
	EventType,
	InteractionRequiredAuthError,
} from "@azure/msal-browser";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";

import { clearSelectedCustomer } from "~/shared/state/user-profile-state.ts";
import { environmentConfig } from "~/shared/utils/environment-config.ts";
import {
	clearLoginOccurrenceId,
	getOrCreateLoginOccurrenceId,
	markLoginEventPending,
} from "~/shared/utils/login-event-session.ts";

/**
 * The host of the B2C authority (e.g. `tqlidentitydev.b2clogin.com`). Accounts
 * that belong to the B2C instance are cached under this environment.
 */
const b2cAuthorityHost = environmentConfig.b2cAuthority
	? new URL(environmentConfig.b2cAuthority).hostname
	: undefined;

/**
 * Whether an account represents its own HOME tenant profile (as opposed to a
 * federated / guest tenant profile).
 *
 * MSAL encodes the home tenant id as the segment after the final "." in
 * `homeAccountId` (format `<localAccountId>.<homeTenantId>`). When a federated
 * Entra → B2C user ALSO has a live Entra session in the same browser (e.g. from
 * the internal app), MSAL caches a SECOND tenant profile for the same B2C home
 * account whose `tenantId` is the upstream Entra tenant. That guest profile has
 * no valid grant against the B2C policy, so silent token acquisition for it
 * makes the B2C `/token` endpoint return 400. Comparing the account's own
 * `tenantId` to its home tenant id lets us keep to the home (B2C) profile.
 */
const isHomeAccount = (account: AccountInfo): boolean => {
	const homeTenantId = account.homeAccountId?.split(".").pop();
	// If we cannot parse a home tenant id, do not exclude the account.
	return !homeTenantId || homeTenantId === account.tenantId;
};

/**
 * Whether an account belongs to the B2C instance (matches the B2C authority
 * host) AND represents its home (B2C) tenant profile rather than a federated
 * guest profile. When the B2C authority is not configured we cannot
 * discriminate by host, so we only require the home-profile check.
 */
const isB2CAccount = (account: AccountInfo): boolean =>
	(!b2cAuthorityHost || account.environment === b2cAuthorityHost) &&
	isHomeAccount(account);

/**
 * Select the account that actually belongs to the B2C instance (home tenant
 * profile). For a federated (Entra → B2C) user MSAL can cache both the correct
 * B2C home account and a guest tenant profile whose `tenantId` points at the
 * upstream Entra tenant. Setting the guest profile active (or acquiring tokens
 * for it) poisons `active-account-filters` with the wrong tenantId and yields a
 * wave of 400s from the B2C token endpoint. We therefore FAIL CLOSED: only a
 * genuine B2C home profile is returned, never a guest/wrong-realm fallback.
 */
const selectB2CAccount = (accounts: AccountInfo[]): AccountInfo | null =>
	accounts.find((account) => isB2CAccount(account)) ?? null;

/**
 * Resolve the B2C home-tenant account for the same user as `account`. When
 * `account` is a federated guest profile (its `tenantId` differs from the home
 * tenant), swap to the matching home profile from the cache so both the active
 * account and every token request are pinned to the correct B2C realm. Returns
 * `null` (fail closed) when no matching home profile is cached rather than
 * falling back to the guest profile, which has no valid B2C grant.
 */
const resolveB2CHomeAccount = (
	instance: IPublicClientApplication,
	account: AccountInfo,
): AccountInfo | null => {
	if (isHomeAccount(account)) {
		return account;
	}

	return (
		instance
			.getAllAccounts()
			.find(
				(candidate) =>
					candidate.homeAccountId === account.homeAccountId &&
					isHomeAccount(candidate),
			) ?? null
	);
};

/**
 * The validated B2C home-tenant account for the current MSAL active account,
 * or `null` when there is no active account or it is a federated guest
 * profile with no cached home profile. Shared by token acquisition and
 * identity/authorization consumers so both resolve the same account rather
 * than trusting cache-array order (`getAllAccounts()[0]`).
 */
export const getActiveB2CAccount = (
	instance: IPublicClientApplication,
): AccountInfo | null => {
	const activeAccount = instance.getActiveAccount();
	return activeAccount ? resolveB2CHomeAccount(instance, activeAccount) : null;
};

const purgeGuestAccount = async (
	instance: IPublicClientApplication,
	account: AccountInfo,
): Promise<boolean> => {
	if (isB2CAccount(account)) {
		console.warn(
			"Skipping cache purge for a valid B2C home account.",
			account.username,
		);
		return false;
	}

	await instance.clearCache({ account });
	return true;
};

const ensureGuestAccountPurged = async (
	instance: IPublicClientApplication,
	account: AccountInfo,
): Promise<boolean> => {
	if (isB2CAccount(account)) {
		return false;
	}

	try {
		return await purgeGuestAccount(instance, account);
	} catch (error) {
		console.error("Failed to purge guest account from MSAL cache:", error);
		return false;
	}
};

type TokenClaims = {
	roles?: string[];
	[key: string]: unknown;
};

const decodeJwtToken = (token: string): TokenClaims | null => {
	try {
		const parts = token.split(".");
		if (parts[1]) {
			const decoded = atob(parts[1]);
			return JSON.parse(decoded) as TokenClaims;
		}
	} catch (error) {
		console.error("Failed to decode JWT token:", error);
	}
	return null;
};

const acquireTokenInteractive = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
): Promise<AuthenticationResult | undefined> => {
	try {
		return await msalInstance.acquireTokenPopup(authenticationRequest);
	} catch {
		await msalInstance.acquireTokenRedirect(authenticationRequest);
		return undefined;
	}
};

const isTokenRefreshError = (error: ClientAuthError): boolean => {
	return (
		error.errorCode === "token_refresh_required" ||
		error.errorMessage?.includes("token_refresh_required") ||
		error.message.includes("token_refresh_required") ||
		error.message.includes("must be refreshed")
	);
};

const handleTokenRefresh = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
	account: AccountInfo,
): Promise<AuthenticationResult | undefined> => {
	try {
		return await msalInstance.acquireTokenSilent({
			...authenticationRequest,
			account,
			forceRefresh: true,
		});
	} catch (refreshError: unknown) {
		console.error(
			"Forced refresh failed, falling back to interactive:",
			refreshError,
		);
		return await acquireTokenInteractive(msalInstance, authenticationRequest);
	}
};

const handleClientAuthError = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
	error: ClientAuthError,
	account: AccountInfo,
): Promise<AuthenticationResult | undefined> => {
	if (isTokenRefreshError(error)) {
		return await handleTokenRefresh(
			msalInstance,
			authenticationRequest,
			account,
		);
	}

	// Handle other ClientAuthErrors - try force refresh
	console.warn(
		"ClientAuthError detected, attempting force refresh as fallback",
	);

	try {
		return await msalInstance.acquireTokenSilent({
			...authenticationRequest,
			account,
			forceRefresh: true,
		});
	} catch (refreshError: unknown) {
		console.error(
			"Force refresh failed, attempting interactive:",
			refreshError,
		);
		return await acquireTokenInteractive(msalInstance, authenticationRequest);
	}
};

const handleUnknownError = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
	account: AccountInfo,
): Promise<AuthenticationResult | undefined> => {
	console.warn(
		"Unknown token acquisition error type, attempting force refresh",
	);

	try {
		return await msalInstance.acquireTokenSilent({
			...authenticationRequest,
			account,
			forceRefresh: true,
		});
	} catch (refreshError: unknown) {
		console.error(
			"Force refresh failed, attempting interactive:",
			refreshError,
		);
		return await acquireTokenInteractive(msalInstance, authenticationRequest);
	}
};

export const getAccessTokenAuthenticationResult = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
): Promise<AuthenticationResult | undefined> => {
	const activeAccount = msalInstance.getActiveAccount();
	if (!activeAccount) {
		throw new Error(
			`No active account! Requested scopes: ${authenticationRequest.scopes.join(", ")}`,
		);
	}

	// Pin token acquisition to the B2C HOME-tenant profile. A federated
	// (Entra → B2C) user can have a guest tenant profile cached whose `tenantId`
	// is the upstream Entra tenant; acquiring a token for that profile makes the
	// B2C `/token` endpoint return 400. Passing the account explicitly (rather
	// than letting MSAL resolve the active account implicitly) keeps every
	// request on the correct realm.
	const account = resolveB2CHomeAccount(msalInstance, activeAccount);
	if (!account) {
		// No B2C home-tenant profile is cached for this user - the active
		// account is a guest profile with no valid B2C grant. Fail closed:
		// purge the poisoned account (not just deselect it) instead of
		// acquiring a token for it, and force a fresh, interactive B2C sign-in.
		console.warn(
			"No B2C home-tenant account cached for the active session; forcing interactive re-authentication.",
		);
		msalInstance.setActiveAccount(null);
		const purged = await ensureGuestAccountPurged(msalInstance, activeAccount);
		if (!purged) {
			throw new Error(
				"Failed to purge invalid B2C guest account from MSAL cache before interactive sign-in.",
			);
		}
		return await acquireTokenInteractive(msalInstance, authenticationRequest);
	}
	// Self-heal a poisoned `active-account-filters` entry (wrong tenantId) so the
	// next `getActiveAccount()` resolves to the home profile too.
	if (account.tenantId !== activeAccount.tenantId) {
		msalInstance.setActiveAccount(account);
	}

	// Check if we should proactively refresh to avoid token_refresh_required exception
	const shouldForceRefresh = account.idTokenClaims?.exp
		? Date.now() / 1000 > account.idTokenClaims.exp - 300 // Refresh if less than 5 min remaining
		: false;

	try {
		return await msalInstance.acquireTokenSilent({
			...authenticationRequest,
			account,
			forceRefresh: shouldForceRefresh,
		});
	} catch (error: unknown) {
		console.error("Token acquisition error:", error);

		if (error instanceof InteractionRequiredAuthError) {
			console.error("Interaction required, prompting user...");
			return await acquireTokenInteractive(msalInstance, authenticationRequest);
		}

		if (error instanceof ClientAuthError) {
			return await handleClientAuthError(
				msalInstance,
				authenticationRequest,
				error,
				account,
			);
		}

		return await handleUnknownError(
			msalInstance,
			authenticationRequest,
			account,
		);
	}
};

export const getAuthorizationHeader = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
): Promise<{ Authorization: string } | undefined> => {
	const result = await getAccessTokenAuthenticationResult(
		msalInstance,
		authenticationRequest,
	);
	return result ? { Authorization: `Bearer ${result.accessToken}` } : undefined;
};

export const getAccessToken = async (
	msalInstance: IPublicClientApplication,
	authenticationRequest: PopupRequest,
): Promise<string | undefined> => {
	const result = await getAccessTokenAuthenticationResult(
		msalInstance,
		authenticationRequest,
	);
	return result?.accessToken;
};

/**
 * A stable fingerprint of a single authentication occurrence: identical across
 * the token-acquisition redirects of one login but different for a genuinely new
 * login, so {@link markLoginEventPending} de-duplicates redirect loops without
 * suppressing a real re-login.
 *
 * Prefer `auth_time` (the authentication instant): it is stable across token
 * refreshes and distinct per login. When the B2C policy omits it we do NOT use
 * `iat` — a re-issued ID token (e.g. the popup→`acquireTokenRedirect` fallback)
 * changes `iat` mid-session, which would look like a new login and duplicate the
 * event. Instead fall back to a session occurrence id that stays stable across
 * token redirects and is reset only at a logout boundary (see
 * `clearLoginOccurrenceId`, called on `LOGOUT_SUCCESS`).
 */
const buildLoginFlowIdentity = (account: AccountInfo): string => {
	const authTime = account.idTokenClaims?.auth_time;
	if (authTime != null) {
		return `${account.homeAccountId}:${authTime}`;
	}
	return getOrCreateLoginOccurrenceId();
};

export const buildLoginFlowIdentityForTest = buildLoginFlowIdentity;

const applyB2CIdentityToAccount = (
	msalInstance: IPublicClientApplication,
	account: AccountInfo,
	clearSelectedCustomerState: () => void,
	onGuestAccountPurgeFailure: (account: AccountInfo) => Promise<void>,
): Promise<void> => {
	clearSelectedCustomerState();
	// Runs only on a genuine B2C login (not on refresh/reload), so this is the
	// authoritative signal that a login-events emission is owed this session. The
	// identity is stable across the redirects of one authentication (same account
	// + `auth_time`) but changes on a genuinely new login, so a redirect loop
	// records exactly one event.
	markLoginEventPending(buildLoginFlowIdentity(account));
	const homeAccount = resolveB2CHomeAccount(msalInstance, account);
	msalInstance.setActiveAccount(homeAccount);
	if (!homeAccount) {
		return onGuestAccountPurgeFailure(account);
	}
	return Promise.resolve();
};

const purgeGuestAccounts = async (
	msalInstance: IPublicClientApplication,
	accounts: AccountInfo[],
): Promise<void> => {
	const purgeResults = await Promise.all(
		accounts
			.filter((account) => !isB2CAccount(account))
			.map((account) => ensureGuestAccountPurged(msalInstance, account)),
	);
	if (purgeResults.some((purged) => !purged)) {
		throw new Error(
			"Failed to purge invalid B2C guest accounts from MSAL cache.",
		);
	}
};

const resolveActiveB2CAccount = async (
	msalInstance: IPublicClientApplication,
): Promise<AccountInfo | null> => {
	const activeAccount = msalInstance.getActiveAccount();
	if (activeAccount && isB2CAccount(activeAccount)) {
		await purgeGuestAccounts(msalInstance, msalInstance.getAllAccounts());
		return activeAccount;
	}

	const allAccounts = msalInstance.getAllAccounts();
	const b2cAccount = selectB2CAccount(allAccounts);
	if (b2cAccount) {
		msalInstance.setActiveAccount(b2cAccount);
		await purgeGuestAccounts(msalInstance, allAccounts);
		return b2cAccount;
	}
	if (allAccounts.length === 0) {
		return null;
	}

	const guestAccounts = allAccounts.filter((account) => !isB2CAccount(account));
	if (guestAccounts.length === 0) {
		msalInstance.setActiveAccount(null);
		return null;
	}

	msalInstance.setActiveAccount(null);
	await purgeGuestAccounts(msalInstance, guestAccounts);

	return null;
};

export const useInitializeMsalInstance = (
	instance: IPublicClientApplication,
	apiTokenRequest: SilentRequest | PopupRequest | undefined,
	setRoles: Dispatch<SetStateAction<Set<string> | undefined>>,
) => {
	const [initializationStatus, setInitializationStatus] = useState<
		"initializing" | "initialized" | "error"
	>("initializing");

	useEffect(() => {
		let isMounted = true;

		const finalizeInitialization = async () => {
			if (apiTokenRequest) {
				try {
					const accessToken = await getAccessToken(instance, apiTokenRequest);
					if (accessToken) {
						const claims = decodeJwtToken(accessToken);
						setRoles(claims?.roles ? new Set(claims.roles) : undefined);
					}
				} catch (error) {
					console.error("Failed to extract roles from token:", error);
				}
			}

			if (isMounted) {
				setInitializationStatus("initialized");
			}
		};

		const handleGuestAccountPurgeFailure = async (
			account: AccountInfo,
		): Promise<void> => {
			const purged = await ensureGuestAccountPurged(instance, account);
			if (!purged) {
				throw new Error(
					"Failed to purge invalid B2C guest account from MSAL cache before completing initialization.",
				);
			}
		};

		const handleLoginSuccess = async (payload: AuthenticationResult) => {
			if (!payload.account) {
				return;
			}

			await applyB2CIdentityToAccount(
				instance,
				payload.account,
				clearSelectedCustomer,
				handleGuestAccountPurgeFailure,
			);
			await finalizeInitialization();
		};

		const handleMsalEventFailure = (error: unknown) => {
			console.error("Failed to process MSAL event:", error);
			if (isMounted) {
				setInitializationStatus("error");
				setRoles(undefined);
			}
		};

		const handleMsalEvent = async (event: EventMessage) => {
			if (!isMounted) return;

			if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
				const payload = event.payload as AuthenticationResult;
				await handleLoginSuccess(payload);
			}

			if (event.eventType === EventType.LOGOUT_SUCCESS) {
				// End the current authentication occurrence so the next login starts a
				// fresh one and is recorded rather than suppressed as a duplicate.
				clearLoginOccurrenceId();
				setInitializationStatus("initializing");
				setRoles(undefined);
			}
		};

		const initializeMsal = async () => {
			try {
				await instance.initialize();
				if (!isMounted) return;

				const redirectResponse = await instance.handleRedirectPromise();
				if (redirectResponse?.account) {
					await applyB2CIdentityToAccount(
						instance,
						redirectResponse.account,
						clearSelectedCustomer,
						handleGuestAccountPurgeFailure,
					);
				}

				if (!isMounted) return;

				instance.addEventCallback((event: EventMessage) => {
					void handleMsalEvent(event).catch(handleMsalEventFailure);
				});

				const activeAccount = await resolveActiveB2CAccount(instance);

				if (activeAccount) {
					await finalizeInitialization();
				} else {
					setInitializationStatus("initialized");
				}
			} catch (error) {
				console.error("Failed to initialize MSAL:", error);
				setInitializationStatus("error");
			}
		};

		void initializeMsal();

		return () => {
			isMounted = false;
		};
	}, [apiTokenRequest, instance, setRoles]);

	return initializationStatus;
};
